/**
 * useSnowballSync
 *
 * Background hook that populates and refreshes the snowballId field on address
 * book contacts.
 *
 * Strategy:
 * - Runs every CONTACT_SYNC_INTERVAL_SECONDS while the app is visible.
 * - Each tick processes up to CONTACT_SYNC_BATCH_SIZE contacts, prioritising
 *   contacts with no snowballId, then the oldest lastSnowballRefreshDate.
 * - Contacts without snowballId are OPRF-hashed and resolved by exact hash to
 *   find their server user ID; contacts that already have one re-confirm it via
 *   OPRF (ensure). Each
 *   resolved contact's lastSnowballRefreshDate is stamped so it isn't
 *   reconsidered until the next day.
 * - Stops automatically once all contacts have been refreshed today.
 * - Immediately re-runs whenever new contacts without snowballId appear
 *   (e.g. after a manual add or vCard import).
 */

import { useEffect, useRef, useCallback } from 'react';
import { useContactState } from '@/modules/contact/ContactStateContext';
import { selectAllContacts } from '@/modules/contact/reducer';
import { ContactLogic, type Contact } from '@/modules/contact';
import { MePrFoService } from '@/services/meprfo';
import {
  CONTACT_SYNC_INTERVAL_SECONDS,
  CONTACT_SYNC_BATCH_SIZE,
  ENV,
} from '@/modules/config';
import { useUserContext } from '@/modules/user/useUserContext';
import { useAppStateChange, useAppActiveRef } from '@/hooks/useAppState';

export function todayISO(): string {
  return new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"
}

/**
 * One ENRICH_CONTACTS payload entry that caches a freshly-resolved snowballId.
 */
export interface ContactEnrichment {
  localId: string;
  snowballId?: string;
  lastSnowballRefreshDate: string;
}

/**
 * Builds a single ENRICH_CONTACTS update for a contact whose server user ID was
 * just resolved. Centralises the reducer contract shared by the background sync
 * and any other flow that resolves snowballIds (e.g. the share wizard's
 * awareness check): always refresh the date, and only set snowballId when it
 * actually changed — passing it undefined leaves the existing value untouched.
 */
export function createContactEnrichment(
  localId: string,
  resolvedSnowballId: string | undefined,
  currentSnowballId: string | undefined,
  today: string = todayISO()
): ContactEnrichment {
  return {
    localId,
    lastSnowballRefreshDate: today,
    ...(resolvedSnowballId !== undefined &&
      resolvedSnowballId !== currentSnowballId && {
        snowballId: resolvedSnowballId,
      }),
  };
}

function normalizePhone(rawPhone: string): string {
  const digits = rawPhone.replace(/\D/g, '');
  // Drop leading "1" for 11-digit US numbers (e.g. "+15551234567" → "5551234567")
  return digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
}

/**
 * Outcome of resolving one contact this cycle.
 * - `enrichment`: the update to cache, or null to skip (transient error → retry
 *   next tick without advancing the refresh date).
 * - `oprfCpuMs`: the OPRF's synchronous CPU time (ms), present iff an OPRF
 *   actually ran to completion — powers the dev sync-stats log.
 */
interface ResolveResult {
  enrichment: ContactEnrichment | null;
  oprfCpuMs?: number;
}

/**
 * Resolve a whole tick's batch of contacts. Contacts with an unusable phone are
 * just date-stamped; the rest are OPRF-hashed, then resolved in two batch calls —
 * one `ensureUsers` (get-or-create) to re-confirm cached-id contacts, and one
 * exact-hash `getUserIdsByOprf` lookup for the uncached ones — so a tick makes at
 * most two resolution round-trips instead of one per contact.
 */
async function resolveBatch(
  contacts: Contact[],
  today: string
): Promise<ResolveResult[]> {
  const results: ResolveResult[] = [];

  // Contacts hashed this tick, split by how they resolve: cached-id contacts are
  // re-confirmed via ensure (their id is the lastKnownUserId hint); uncached contacts
  // are looked up by exact hash (never created). oprfCpuMs is captured per
  // contact for the dev-stats log.
  const pendingEnsures: {
    contact: Contact;
    hash: string;
    phoneTail: string;
    oprfCpuMs?: number;
  }[] = [];
  const pendingLookups: {
    contact: Contact;
    hash: string;
    oprfCpuMs?: number;
  }[] = [];

  // Phase 1 (concurrent): stamp invalid phones and hash the rest. Hashing
  // failures are isolated per contact.
  await Promise.all(
    contacts.map(async contact => {
      const rawPhone = ContactLogic.getPrimaryPhone(contact);
      if (!rawPhone) {
        results.push({ enrichment: null });
        return;
      }
      const normalized = normalizePhone(rawPhone);

      // Phone can't be hashed (not 10 digits) — mark it refreshed so we don't
      // keep reconsidering it every tick. No OPRF runs.
      if (!/^\d{10}$/.test(normalized)) {
        results.push({
          enrichment: createContactEnrichment(
            contact.localId,
            undefined,
            contact.snowballId,
            today
          ),
        });
        return;
      }

      let oprfCpuMs: number | undefined;
      try {
        const hashResult = await MePrFoService.computeDeterministicHash(
          normalized,
          {
            onCpuTime: ms => {
              oprfCpuMs = ms;
            },
          }
        );
        if (contact.snowballId) {
          pendingEnsures.push({
            contact,
            hash: hashResult.hash,
            phoneTail: hashResult.phoneTail,
            oprfCpuMs,
          });
        } else {
          pendingLookups.push({ contact, hash: hashResult.hash, oprfCpuMs });
        }
      } catch {
        // OPRF error — skip this contact this cycle (retries next tick).
        results.push({ enrichment: null, oprfCpuMs });
      }
    })
  );

  // Phase 2a: re-confirm cached-id contacts in one batch ensure. Each passes its
  // cached id as the lastKnownUserId hint, so ensure returns the live (or migrated) id.
  if (pendingEnsures.length > 0) {
    try {
      const idsByOprf = await MePrFoService.ensureUsers(
        pendingEnsures.map(p => ({
          lastKnownUserId: p.contact.snowballId as string,
          phoneTail: p.phoneTail,
          phoneOprf: p.hash,
        }))
      );
      for (const { contact, hash, oprfCpuMs } of pendingEnsures) {
        results.push({
          enrichment: createContactEnrichment(
            contact.localId,
            idsByOprf.get(hash),
            contact.snowballId,
            today
          ),
          oprfCpuMs,
        });
      }
    } catch {
      // Batch ensure failed — skip these this cycle (retries next tick). The
      // OPRF cost was still paid, so it should count toward the dev stats.
      for (const { oprfCpuMs } of pendingEnsures) {
        results.push({ enrichment: null, oprfCpuMs });
      }
    }
  }

  // Phase 2b: resolve every uncached contact by exact OPRF hash in one batch
  // lookup (never creates). A hash absent from the result → not a Snowball user,
  // so snowballId stays unset and we just stamp the refresh date.
  if (pendingLookups.length > 0) {
    try {
      const idsByHash = await MePrFoService.getUserIdsByOprf(
        pendingLookups.map(p => p.hash)
      );
      for (const { contact, hash, oprfCpuMs } of pendingLookups) {
        results.push({
          enrichment: createContactEnrichment(
            contact.localId,
            idsByHash.get(hash),
            contact.snowballId,
            today
          ),
          oprfCpuMs,
        });
      }
    } catch {
      // Batch lookup failed — skip these this cycle (retries next tick). The
      // OPRF cost was still paid, so it should count toward the dev stats.
      for (const { oprfCpuMs } of pendingLookups) {
        results.push({ enrichment: null, oprfCpuMs });
      }
    }
  }

  return results;
}

/**
 * Running totals for the dev-only OPRF cost log, accumulated across the tab's
 * lifetime (`performance.now()` is ms since the tab loaded).
 */
interface SyncStats {
  oprfCount: number;
  oprfCpuMsTotal: number;
}

function logSyncStats(stats: SyncStats): void {
  const openMinutes = performance.now() / 60_000;
  const openHours = openMinutes / 60;
  const ratePerHour = openHours > 0 ? stats.oprfCount / openHours : 0;
  const avgCpuMs =
    stats.oprfCount > 0 ? stats.oprfCpuMsTotal / stats.oprfCount : 0;
  // eslint-disable-next-line no-console
  console.log(
    `[snowball-sync] ${stats.oprfCount} OPRFs since tab open | ` +
      `open ${openMinutes.toFixed(1)} min | ` +
      `${ratePerHour.toFixed(0)}/hr | ` +
      `avg ${avgCpuMs.toFixed(2)} ms CPU/OPRF`
  );
}

export function useSnowballSync(): void {
  const { state, dispatch, isLoading } = useContactState();
  const { loading: authLoading } = useUserContext();

  // Keep a ref to the latest context so runSync can be a stable callback
  const contextRef = useRef({ state, dispatch, isLoading, authLoading });
  useEffect(() => {
    contextRef.current = { state, dispatch, isLoading, authLoading };
  });

  const isRunningRef = useRef(false);

  // Dev-only running totals of OPRF work since the tab opened (see logSyncStats).
  const statsRef = useRef<SyncStats>({ oprfCount: 0, oprfCpuMsTotal: 0 });

  // Tracks foreground/background state via Capacitor's appStateChange so the
  // sync skips work while the app is backgrounded (web + native).
  const isActiveRef = useAppActiveRef();

  const runSync = useCallback(async () => {
    if (isRunningRef.current) return;
    const {
      state: currentState,
      dispatch: currentDispatch,
      isLoading: loading,
      authLoading: authIsLoading,
    } = contextRef.current;
    if (authIsLoading || loading) return;

    isRunningRef.current = true;
    try {
      const today = todayISO();
      const allContacts = selectAllContacts(currentState);

      // Find contacts not yet refreshed today, sorted oldest-refresh-date first.
      // undefined (never refreshed) sorts before any date string.
      const pending = allContacts
        .filter(c => {
          if (c.lastSnowballRefreshDate === today) return false;
          // Only process contacts that have a usable phone number
          return ContactLogic.getPrimaryPhone(c) !== null;
        })
        .sort((a, b) => {
          if (!a.lastSnowballRefreshDate && !b.lastSnowballRefreshDate)
            return 0;
          if (!a.lastSnowballRefreshDate) return -1;
          if (!b.lastSnowballRefreshDate) return 1;
          return a.lastSnowballRefreshDate < b.lastSnowballRefreshDate ? -1 : 1;
        });

      if (pending.length === 0) return;

      // Resolve a bounded batch per tick (oldest-refreshed first). Batching
      // warms the snowballId cache faster than one-at-a-time — so the share
      // wizard's past-recipient match finds contacts already resolved and can
      // skip OPRF — while the cap keeps a tick's work from janking the UI.
      const batch = pending.slice(0, CONTACT_SYNC_BATCH_SIZE);
      const results = await resolveBatch(batch, today);

      const enrichments = results
        .map(r => r.enrichment)
        .filter((e): e is ContactEnrichment => e !== null);
      if (enrichments.length > 0) {
        currentDispatch({ type: 'ENRICH_CONTACTS', payload: enrichments });
      }

      // Dev diagnostics: accumulate this tick's OPRF work and print running
      // stats whenever an OPRF actually ran.
      if (ENV.isDevelopment) {
        let ranThisTick = false;
        for (const { oprfCpuMs } of results) {
          if (oprfCpuMs !== undefined) {
            statsRef.current.oprfCount += 1;
            statsRef.current.oprfCpuMsTotal += oprfCpuMs;
            ranThisTick = true;
          }
        }
        if (ranThisTick) logSyncStats(statsRef.current);
      }
    } finally {
      isRunningRef.current = false;
    }
  }, []); // stable — reads from contextRef

  // Periodic sync on a fixed interval (only while the app is foregrounded)
  useEffect(() => {
    if (isActiveRef.current) {
      runSync();
    }

    const intervalId = setInterval(() => {
      if (isActiveRef.current) {
        runSync();
      }
    }, CONTACT_SYNC_INTERVAL_SECONDS * 1000);

    return () => clearInterval(intervalId);
  }, [runSync, isActiveRef]);

  // Re-run when the app returns to the foreground after being backgrounded
  useAppStateChange(isActive => {
    if (isActive) {
      runSync();
    }
  });

  // Immediately trigger a sync when new contacts are added (allIds grows).
  // Watching allIds.length — not unresolvedCount — ensures we only fire once
  // per batch add, not after every individual ENRICH_CONTACTS dispatch.
  const contactCount = state.allIds.length;
  useEffect(() => {
    if (contactCount > 0 && !isLoading) {
      runSync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactCount, isLoading]);
}
